import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Live preview of the space an organizer is claiming, rendered as the two
 * screens their attendees will actually use.
 *
 * Mirrored from the app as it stands today, not from a design deliverable:
 * `lib/pages/events/event_page.dart` (the app bar with Edit in it, the member
 * bar, the "Event details" toggle) and `lib/widgets/aurora.dart`
 * (AuroraCard / AuroraInfoCard / AuroraChip). Colours come from
 * `lib/utils/aurora_palette.dart` - TealPalette. **The app is the source of
 * truth**; if a screen there moves, this is stale and should follow it.
 *
 * Two things the app does that this deliberately copies rather than improves:
 *
 *  - WHERE follows the app's fallback chain: the venue's name leads, its
 *    address sits beneath, and where only one is given it takes the headline.
 *  - The Agenda button appears only when a brochure exists, which is the app's
 *    own "no brochure, no button" guard.
 *
 * Honesty rule for the two panels: the details screen is bound to the
 * organizer's real, live form values - edit the name or swap the logo and it
 * updates. The member list cannot be, because an unclaimed space has nobody in
 * it, so its people are labelled Example and the details screen shows the real
 * empty state rather than a fabricated count.
 */
@Component({
  selector: 'app-space-preview',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="preview">
      <!--
        Deliberately conditional wording. An organizer reading a cold invitation
        has not agreed to anything yet, and copy that asserts "your space" as a
        present fact reads as though something was already published in their
        name. Everything here is a sketch until they claim it, so the heading
        says so.
      -->
      <div class="preview-head">
        <div>
          <div class="preview-kicker">A glimpse of how your event could look</div>
          <div class="preview-sub">
            Nothing is published yet &mdash; this is just a sketch that follows along as you
            edit, so you can see what claiming would give you.
          </div>
        </div>
      </div>

      <div class="phones">
        <!-- Event details: bound to the organizer's real values -->
        <figure class="phone-figure">
          <div class="example-label" aria-hidden="true"></div>
          <div class="phone">
            <div class="sbar"><span>9:41</span><span>•••</span></div>
            <!-- Edit sits in the bar, not behind an overflow menu: it is the one
                 action only the organizer has, and the one they come back for. -->
            <div class="abar">
              <span class="glyph">←</span>
              <span class="atitle">{{ displayTitle }}</span>
              <span class="barbtn">Edit</span>
            </div>

            <div class="screen">
              <div class="agenda" *ngIf="brochureUrl">Agenda</div>

              <!-- About: a status line, then the description. One line of small
                   labels with a middot, not pills - they say what the event is,
                   they are not filters you can press. -->
              <div class="pcard">
                <div class="statusline">
                  <span class="st-amber" *ngIf="whenLabel">{{ whenLabel }}</span>
                  <span class="st-dot" *ngIf="whenLabel">·</span>
                  <span class="st-accent">{{ typeLabel }}</span>
                </div>
                <div class="rl clamp2 mt6">
                  {{ description || 'Your event description appears here.' }}
                </div>
              </div>

              <div class="row gap8">
                <div class="pcard grow">
                  <div class="cardlab">WHEN</div>
                  <div class="cardval">{{ dateRangeLabel }}</div>
                </div>
                <div class="pcard grow">
                  <div class="cardlab">WHO</div>
                  <div class="cardval">{{ memberCount ? memberCount + ' members' : 'Members' }}</div>
                  <div class="rl accent" *ngIf="memberCount">{{ memberCount }} near you</div>
                </div>
              </div>

              <!--
                The map is always drawn, even with no address. It is the thing
                an organizer is being shown they would get, and an empty card
                illustrates nothing. The copy underneath stays honest about the
                state, so the picture is an example rather than a claim: with a
                location it names it, without one it says what to do.
              -->
              <div class="pcard flush">
                <div class="pad">
                  <div class="cardlab">WHERE</div>
                  <div class="cardval">{{ whereHeadline }}</div>
                  <div class="rl" *ngIf="whereDetail">{{ whereDetail }}</div>
                  <div class="rl faint" *ngIf="!hasLocation">Add a venue and it pins here</div>
                </div>
                <!--
                  Drawn, not gridded. Straight strips at right angles read as a
                  spreadsheet; what makes a thumbnail legible as a map at 96px
                  is the mix of shapes - a river bending through it, a park, and
                  roads that curve and meet at angles. Everything is in palette,
                  so it sits in the card instead of looking like a screenshot of
                  somewhere else.
                -->
                <div class="map">
                  <svg viewBox="0 0 224 96" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                    <rect width="224" height="96" fill="#E7EEEA" />
                    <ellipse cx="188" cy="22" rx="34" ry="20" fill="#D5E4CF" />
                    <ellipse cx="26" cy="84" rx="30" ry="16" fill="#D5E4CF" />
                    <path d="M-6 76 C 34 64 66 90 106 72 S 188 48 230 58"
                          fill="none" stroke="#C3DCD6" stroke-width="14" stroke-linecap="round" />
                    <g fill="#DAE4DF">
                      <rect x="14" y="50" width="22" height="15" rx="2" />
                      <rect x="41" y="53" width="15" height="11" rx="2" />
                      <rect x="8" y="24" width="19" height="10" rx="2" />
                      <rect x="86" y="20" width="21" height="12" rx="2" />
                      <rect x="140" y="58" width="19" height="12" rx="2" />
                      <rect x="164" y="62" width="13" height="9" rx="2" />
                      <rect x="46" y="8" width="16" height="9" rx="2" />
                    </g>
                    <g fill="none" stroke="#FFFFFF" stroke-linecap="round">
                      <path d="M-6 44 C 58 39 148 51 230 41" stroke-width="7.5" />
                      <path d="M76 -6 L 68 102" stroke-width="5" />
                      <path d="M132 -6 L 172 102" stroke-width="4" />
                      <path d="M-6 18 L 230 11" stroke-width="2.5" opacity="0.9" />
                      <path d="M104 44 L 112 -6" stroke-width="2.5" opacity="0.9" />
                    </g>
                    <ellipse cx="112" cy="60" rx="7" ry="2.4" fill="#12332F" opacity="0.22" />
                    <path d="M112 25c-7.2 0-13 5.8-13 13 0 9.6 13 21 13 21s13-11.4 13-21c0-7.2-5.8-13-13-13z"
                          fill="#1F6F66" />
                    <circle cx="112" cy="38" r="4.4" fill="#FFFFFF" />
                  </svg>
                </div>
              </div>
            </div>

            <!-- The panel's edge. A labelled control, not a bare grab bar. -->
            <div class="toggle-wrap"><div class="toggle">Event details <span class="chev">⌄</span></div></div>
          </div>
          <figcaption>Your event details, as attendees would see them</figcaption>
        </figure>

        <!-- Members: illustrative, and labelled as such -->
        <figure class="phone-figure">
          <div class="example-label">Example of a filled space</div>
          <div class="phone">
            <div class="sbar"><span>9:41</span><span>•••</span></div>
            <div class="abar">
              <span class="glyph">←</span>
              <span class="atitle">{{ displayTitle }}</span>
              <span class="barbtn">Edit</span>
            </div>

            <!-- The bar over the member list: the space's mark, how many people
                 are here, how many are close, and the two things you do to a
                 list of them. -->
            <div class="memberbar">
              <div class="av av-lg" [class.av-empty]="!logoUrl">
                <img *ngIf="logoUrl" [src]="logoUrl" alt="" />
              </div>
              <div class="grow">
                <div class="countline">4 members</div>
                <div class="rl">2 near you</div>
              </div>
              <span class="icobtn">⌕</span>
              <span class="icobtn">☰</span>
              <span class="icobtn">⋮</span>
            </div>

            <div class="screen tightgap">
              <div class="pcard row gap9 center" *ngFor="let m of examples">
                <div class="av av-initials" [style.background]="m.tint">{{ m.initials }}</div>
                <div class="grow">
                  <div class="nm">{{ m.name }}</div>
                  <div class="rl">{{ m.role }}</div>
                  <div class="rl faint">{{ m.industry }}</div>
                </div>
                <div class="col end gap4">
                  <span class="au-chip au-chip--status" *ngIf="m.organizer">Organizer</span>
                  <span class="au-chip" [class.au-chip--metric]="m.distance" [class.au-chip--muted]="!m.distance">
                    {{ m.distance || 'Not nearby' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <figcaption>The kinds of people your attendees come to meet &mdash; your own
            space starts empty</figcaption>
        </figure>
      </div>

      <p class="preview-foot">Your shareable link and QR code arrive on the next step.</p>
    </div>
  `,
  styles: [`
    /* TealPalette, mirrored from lib/utils/aurora_palette.dart.
       The ground you are on decides the accent: --accent is 2.02 on cream and
       6.33 on --ground, so it appears only on dark; --accent-deep is 5.58 on
       cream and carries every accent element here, text included. */
    :host {
      --accent: #6DBFB3;
      --accent-deep: #1F6F66;
      --ground: #12332F;
      --ground-lift: #1B4A43;
      --amber: #FFB03A;
      --amber-ink: #8A5A08;
      --cream: #FFF6EE;
      --ink: #5E5A57;
      --faint: #8F8C88;
      --disp: 'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif;
      --body: 'Instrument Sans', system-ui, -apple-system, sans-serif;
      display: block;
    }

    .preview {
      background: linear-gradient(135deg, var(--cream), #E7F1EE);
      border-radius: 18px;
      padding: 22px clamp(16px, 3vw, 30px) 26px;
    }

    .preview-kicker {
      font: 800 19px/1.15 var(--disp);
      color: var(--ground);
      letter-spacing: -0.01em;
    }

    .preview-sub {
      font: 400 13px var(--body);
      color: var(--ink);
      margin-top: 3px;
    }

    .preview-head { margin-bottom: 18px; }

    .phones {
      display: flex;
      gap: clamp(14px, 3vw, 28px);
      flex-wrap: wrap;
      justify-content: center;
    }

    .phone-figure { margin: 0; display: flex; flex-direction: column; align-items: center; gap: 8px; }

    figcaption {
      font: 400 11.5px var(--body);
      color: var(--ink);
      text-align: center;
      max-width: 250px;
    }

    .phone {
      width: 250px;
      height: 530px;
      border-radius: 34px;
      border: 7px solid var(--ground);
      background: var(--cream);
      overflow: hidden;
      position: relative;
      box-shadow: 0 18px 44px rgba(18, 51, 47, 0.22);
      flex-shrink: 0;
    }

    .sbar {
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 13px;
      font: 600 9px var(--body);
      color: var(--ground);
    }

    /* Cream, and one colour - the app bar carries no dark band of its own. */
    .abar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 5px 13px 9px;
    }

    .glyph { font: 700 13px var(--disp); color: var(--ink); flex-shrink: 0; }

    .atitle {
      font: 800 13px var(--disp);
      color: var(--ground);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      text-align: center;
    }

    .barbtn {
      font: 700 10px var(--disp);
      color: var(--accent-deep);
      flex-shrink: 0;
    }

    .screen { padding: 0 13px; display: flex; flex-direction: column; gap: 8px; }
    .screen.tightgap { gap: 7px; }

    /* AuroraCard: white, radius 16dp, the soft ground-tinted lift. */
    .pcard {
      background: #fff;
      border-radius: 11px;
      padding: 9px 10px;
      box-shadow: 0 3px 8px rgba(18, 51, 47, 0.07);
    }

    .pcard.flush { padding: 0; overflow: hidden; }
    .pad { padding: 9px 10px; }

    .agenda {
      background: var(--accent-deep);
      color: var(--cream);
      border-radius: 9px;
      padding: 8px 0;
      text-align: center;
      font: 700 11px var(--disp);
    }

    .row { display: flex; }
    .col { display: flex; flex-direction: column; }
    .center { align-items: center; }
    .end { align-items: flex-end; }
    .grow { flex: 1; min-width: 0; }
    .gap4 { gap: 4px; } .gap8 { gap: 8px; } .gap9 { gap: 9px; }
    .mt6 { margin-top: 6px; }

    .statusline { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
    .st-amber { font: 700 9.5px var(--body); color: var(--amber-ink); }
    .st-accent { font: 700 9.5px var(--body); color: var(--accent-deep); }
    .st-dot { font: 400 9.5px var(--body); color: var(--faint); }

    .av {
      width: 32px;
      height: 32px;
      border-radius: 11px;
      flex-shrink: 0;
      overflow: hidden;
      background: linear-gradient(135deg, var(--amber), var(--accent-deep));
    }

    .av img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .av-lg { width: 40px; height: 40px; border-radius: 12px; }
    .av-empty { background: linear-gradient(135deg, #DCE7E5, #C3D4D0); }

    /* The app's InitialsAvatar: a tone graded toward the ground, white initials
       over it. Each tone is picked to clear 4.5:1 against white at its lightest
       band, so the letters hold at every point of the gradient. */
    .av-initials {
      display: flex;
      align-items: center;
      justify-content: center;
      font: 700 11px var(--disp);
      color: #fff;
      letter-spacing: 0.02em;
    }

    .nm { font: 700 12px var(--disp); color: var(--ground); }

    .rl { font: 400 9.5px/1.35 var(--body); color: var(--ink); }
    .rl.faint { color: var(--faint); }
    .rl.accent { color: var(--accent-deep); }

    .clamp2 {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    /* AuroraInfoCard's label: uppercase, amber, tracked. */
    .cardlab {
      font: 700 8px var(--disp);
      color: var(--amber-ink);
      letter-spacing: 0.12em;
    }

    .cardval { font: 400 11px var(--disp); color: var(--ground); margin-top: 3px; }

    .memberbar {
      display: flex;
      align-items: center;
      gap: 9px;
      padding: 4px 13px 12px;
    }

    .countline { font: 700 11.5px var(--disp); color: var(--ground); }
    .icobtn { font: 600 11px var(--body); color: var(--ink); flex-shrink: 0; }

    .au-chip {
      display: inline-flex;
      align-items: center;
      font: 600 9px var(--body);
      padding: 3px 8px;
      border-radius: 999px;
      white-space: nowrap;
    }

    /* Deep, not light: the light accent is 2.02 on cream and never carries text.
       8% tint, matching AppColors.chipMetricFill (Palette.accentWash). */
    .au-chip--metric { background: rgba(109, 191, 179, 0.16); color: var(--accent-deep); }
    .au-chip--status { background: rgba(255, 176, 58, 0.2); color: var(--amber-ink); }
    /* Absent distance reads as a state, not a gap. */
    .au-chip--muted { background: rgba(18, 51, 47, 0.07); color: var(--ink); }

    .map {
      height: 96px;
      position: relative;
      overflow: hidden;
      background: #E7EEEA;
    }

    .map svg { display: block; width: 100%; height: 100%; }

    .toggle-wrap {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--cream);
      box-shadow: 0 -3px 10px rgba(18, 51, 47, 0.07);
      padding: 9px 0 11px;
    }

    .toggle {
      text-align: center;
      font: 700 10.5px var(--disp);
      color: var(--accent-deep);
    }

    .chev { font-size: 12px; }

    /* Present in both figures, empty in one, so the phones share a baseline. */
    .example-label {
      font: 500 11px var(--body);
      line-height: 1.3;
      min-height: 1.3em;
      color: var(--ink);
      margin-bottom: 2px;
    }

    .preview-foot {
      margin: 16px 0 0;
      text-align: center;
      font: 400 12px var(--body);
      color: var(--ink);
    }

    @media (max-width: 620px) {
      .phones { flex-direction: column; align-items: center; }
    }
  `],
})
export class SpacePreviewComponent {
  @Input() logoUrl: string | null = null;
  @Input() title = '';
  @Input() description = '';
  @Input() startDate: string | null = null;
  @Input() endDate: string | null = null;
  @Input() venueName = '';
  @Input() locationDescription = '';
  @Input() type: string | null = 'Event';
  /* The app hides the Agenda button when there is no brochure; so does this. */
  @Input() brochureUrl = '';
  /* Zero until someone joins; the preview shows the real empty state, not a guess. */
  @Input() memberCount = 0;

  /*
   * Deliberately generic: initials-style gradient avatars and role/firm lines
   * rather than invented full identities with faces. They exist to show the
   * shape of the screen, and the caption says so.
   */
  /*
   * Tones and gradients are the app's InitialsAvatar recipe: one of its curated
   * tones graded 45% toward the ground, white initials over it. Every tone
   * clears 4.5:1 against white at its lightest band. Initials match the ones
   * the campaign's hero image uses, so the email and this page show the same
   * room rather than two different casts.
   */
  readonly examples = [
    {
      name: 'Investment Director',
      role: 'Private equity · Iberia',
      industry: 'Finance',
      distance: '260 m',
      organizer: false,
      initials: 'PE',
      tint: 'linear-gradient(135deg,#1F6F66,#19544D)',
    },
    {
      name: 'You',
      role: 'Event organizer',
      industry: 'Conferences & events',
      distance: '6 km',
      organizer: true,
      initials: 'ME',
      tint: 'linear-gradient(135deg,#8A5A08,#54481A)',
    },
    {
      name: 'Head of Acquisitions',
      role: 'Real estate group',
      industry: 'Real Estate',
      distance: '5 km',
      organizer: false,
      initials: 'RE',
      tint: 'linear-gradient(135deg,#2E6A36,#215133)',
    },
    {
      name: 'Portfolio Manager',
      role: 'Institutional allocator',
      industry: 'Finance',
      distance: null,
      organizer: false,
      initials: 'IM',
      tint: 'linear-gradient(135deg,#185A53,#154843)',
    },
  ];

  get displayTitle(): string {
    return this.title?.trim() || 'Your event';
  }

  get typeLabel(): string {
    return this.type === 'Community' ? 'Community' : 'In person';
  }

  /**
   * The app's own status line: "Happening now" while it runs, "Happening this
   * week" in the seven days before, "Ended" after. Nothing when it is further
   * out, because "in 4 months" is not a status.
   */
  get whenLabel(): string {
    const start = this.toDate(this.startDate);
    if (!start) return '';
    const end = this.toDate(this.endDate) ?? start;
    const now = new Date();
    const endsAt = new Date(end.getTime() + 24 * 60 * 60 * 1000);
    if (now >= start && now <= endsAt) return 'Happening now';
    if (endsAt < now) return 'Ended';
    const days = (start.getTime() - now.getTime()) / (24 * 60 * 60 * 1000);
    return days <= 7 ? 'Happening this week' : '';
  }

  /** "12 - 14 Nov 2026" within one month, "14 Nov 2026 → 3 Dec 2026" across. */
  get dateRangeLabel(): string {
    const start = this.toDate(this.startDate);
    if (!start) return 'Add a start date';
    const end = this.toDate(this.endDate);
    if (!end || end.getTime() === start.getTime()) return this.day(start);
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      return `${start.getDate()} - ${this.day(end)}`;
    }
    return `${this.day(start)} → ${this.day(end)}`;
  }

  /*
   * The app's own fallback chain (buildWhereCard): the venue's name leads, its
   * address sits beneath, and where only one is given it takes the headline
   * rather than leaving an empty row.
   */
  get hasLocation(): boolean {
    return !!(this.venueName?.trim() || this.locationDescription?.trim());
  }

  get whereHeadline(): string {
    return this.venueName?.trim() || this.locationDescription?.trim() || 'No venue yet';
  }

  get whereDetail(): string {
    const name = this.venueName?.trim();
    const address = this.locationDescription?.trim();
    return name && address ? address : '';
  }

  private day(d: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }

  private toDate(value: string | null): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
